import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { OrganizationSampleDataSummary } from './OrganizationSampleDataSummary';
import { RemoveSampleDataModal } from './RemoveSampleDataModal';
import { SampleDataSkippedList } from './SampleDataSkippedList';
import { useOrganizationSampleData } from './useOrganizationSampleData';
import { AdminPanel } from './AdminPage';
import './OrganizationSampleDataPanel.css';

interface OrganizationSampleDataPanelProps {
  organizationId: string;
}

/**
 * System admin view of one organization's sample data: what was seeded, and controls to add a set,
 * remove it, or retry sample calls that failed. Hides itself entirely on an API build that predates the endpoint.
 *
 * An AdminPanel, so it reads as one more section of the organization page.
 */
export const OrganizationSampleDataPanel = ({ organizationId }: OrganizationSampleDataPanelProps) => {
  const titleId = useId();
  const { load, pending, feedback, retry, add, remove, retryCalls } =
    useOrganizationSampleData(organizationId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const focusedFeedbackRef = useRef<typeof feedback>(null);

  /*
    A successful add, remove or calls retry unmounts the button that was used, so the button focus
    was on (or that ConfirmModal hands focus back to) disappears and focus falls to <body>. Moving
    it to the heading once the action has settled and the modal is gone keeps a keyboard user in
    place.
    Waiting for the modal matters: its unmount restores focus to "Remove sample data", which is
    still mounted when the DELETE response carried no status and a refetch is pending.
  */
  useEffect(() => {
    if (feedback?.kind !== 'success' || pending !== null || confirmOpen) return;
    if (focusedFeedbackRef.current === feedback) return;
    focusedFeedbackRef.current = feedback;
    titleRef.current?.focus();
  }, [feedback, pending, confirmOpen]);

  if (load.kind === 'unavailable') return null;

  const status = load.kind === 'ready' ? load.status : null;
  const busy = pending !== null;

  const handleConfirmRemove = async () => {
    await remove();
    // Closed on failure too: the error is shown in the panel, next to the button that retries it.
    setConfirmOpen(false);
  };

  return (
    <AdminPanel
      className="org-sample-data"
      title="Sample data"
      titleId={titleId}
      titleRef={titleRef}
      actions={
        status?.active ? (
          <Button
            variant="secondary"
            onClick={() => setConfirmOpen(true)}
            disabled={busy}
            aria-busy={pending === 'remove' || undefined}
            data-testid="sample-data-remove"
          >
            {pending === 'remove' ? 'Removing...' : 'Remove sample data'}
          </Button>
        ) : status ? (
          <Button
            variant="primary"
            onClick={() => void add()}
            disabled={busy}
            aria-busy={pending === 'add' || undefined}
            data-testid="sample-data-add"
          >
            {pending === 'add' ? 'Adding...' : 'Add sample data'}
          </Button>
        ) : undefined
      }
    >
      {load.kind === 'loading' && <p className="admin-state">Loading sample data...</p>}

      {load.kind === 'failed' && (
        <div className="admin-error" role="alert">
          <span>{load.message}</span>
          <Button variant="secondary" onClick={retry} data-testid="sample-data-load-retry">
            Retry
          </Button>
        </div>
      )}

      {feedback?.kind === 'error' && (
        <div className="admin-error" role="alert">
          {feedback.text}
        </div>
      )}

      {/* Always mounted: a live region inserted along with its text is not reliably announced. */}
      <p className="org-sample-data__result" role="status" data-testid="sample-data-result">
        {feedback?.kind === 'success' ? feedback.text : ''}
      </p>
      {feedback?.kind === 'success' && <SampleDataSkippedList items={feedback.skipped} />}

      {status?.active && (
        <OrganizationSampleDataSummary
          status={status}
          retryCalls={{
            onRetry: () => void retryCalls(),
            running: pending === 'retryCalls',
            disabled: busy,
          }}
        />
      )}
      {status && !status.active && (
        <p className="admin-state">This organization has no sample data.</p>
      )}

      {status?.active && (
        <RemoveSampleDataModal
          isOpen={confirmOpen}
          preview={status.removalPreview}
          busy={pending === 'remove'}
          onConfirm={handleConfirmRemove}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </AdminPanel>
  );
};
